import * as atoms from '@/config/globalStateControl';

import {
  ActionIcon,
  Box,
  Divider,
  Group,
  Indicator,
  Modal,
  Popover,
  ScrollArea,
  Stack,
  createStyles,
} from '@mantine/core';
import Button, { ButtonTypes } from './Buttons/Button';
import { IconBell, IconX } from '@tabler/icons-react';
import { isDark, timeForToday } from '@/utils/utils';
import { useAtom, useAtomValue } from 'jotai';
import { useClickOutside, useDisclosure } from '@mantine/hooks';
import { useEffect, useState } from 'react';

import { API_ENDPOINT } from '@/config/endpoint';
import DMText from './DMText';
import Link from 'next/link';
import { mixpanelTrack } from '@/utils/mixpanel';
import themeVariables from '@/styles/themeVariables';
import { useMediaQuery } from '@mantine/hooks';

const useNotificationBellStyle = createStyles((_, dark) => ({
  popoverContainer: {
    boxShadow: themeVariables.boxShadow.popOverShadow,
    border: `1px solid ${themeVariables.colors.light200}`,
  },

  notificationItemContainer: {
    borderRadius: '0.5rem',
    padding: 12,
    '&:hover': {
      backgroundColor: dark?.dark
        ? themeVariables.colors.darkHover
        : themeVariables.colors.light100,
    },
  },

  notificationTitle: {
    fontWeight: 700,
  },

  notificationItemHeader: {
    display: 'flex',
    color: themeVariables.fontColors.dark,
    fontSize: 14,
    marginBottom: 8,
  },

  notificationItemDot: {
    display: 'block',
    width: 6,
    height: 5,
    backgroundColor: '#0070F3',
    borderRadius: '100%',
    margin: 'auto 12px auto 0px',
  },

  notificationItemFooter: {
    textAlign: 'right',
    fontSize: 13,
  },

  showMoreButtonContainer: {
    padding: '12px 0px',
    textAlign: 'center',
  },

  emptyContentText: {
    padding: '16px 32px 32px 32px',
    textAlign: 'center',
    color: themeVariables.fontColors.dark,
    fontSize: 14,
  },
}));

const NotificationBell = () => {
  const { dark } = isDark();
  const { classes } = useNotificationBellStyle({ dark });

  const isMobile = useMediaQuery(themeVariables.layout.mobile);
  const isSignedIn = useAtomValue(atoms.isSignedIn);

  const [notifications, setNotifications] = useAtom(atoms.notifications);
  const [open, openHandler] = useDisclosure(false);

  const popoverRef = useClickOutside(() => openHandler.close(), ['mouseup']);

  useEffect(() => {
    if (isSignedIn) {
      fetchNotification();
    }
  }, [isSignedIn]);

  const fetchNotification = async () => {
    const redDotResponse = await API_ENDPOINT.notification.getRedDot();
    const notificationResponse =
      await API_ENDPOINT.notification.getNotifications(5, 1);

    setNotifications({
      redDot: redDotResponse?.data,
      data: notificationResponse?.data.notifications,
      totalCount: notificationResponse?.data.total_count,
    });
  };

  if (!isSignedIn) return null;

  if (isMobile) {
    return (
      <>
        <NotificationButton
          notifications={notifications}
          setNotifications={setNotifications}
          openHandler={openHandler}
        />
        <Modal
          centered
          fullScreen
          opened={open}
          withCloseButton={false}
          transitionProps={{ duration: 0 }}
          onClose={() => openHandler.close()}
          classNames={{
            content: `hide-scrollbar`,
            inner: {
              backgroundColor: 'white',
            },
          }}
          styles={{
            content: {
              backgroundColor: dark
                ? themeVariables.colors.dark
                : themeVariables.colors.white,
            },
          }}
          size={'auto'}
          zIndex={2000}
        >
          <Group m={'32px 16px 24px 16px'}>
            <DMText
              className={classes.notificationTitle}
              dColor={themeVariables.darkMode.strong}
            >
              알림
            </DMText>
            <ActionIcon
              size={36}
              ml={'auto'}
              onClick={() => openHandler.close()}
              aria-label="exit-icon"
            >
              <IconX size={21} stroke={2} />
            </ActionIcon>
          </Group>
          <NotificationBody
            notifications={notifications}
            classes={classes}
            setNotifications={setNotifications}
            openHandler={openHandler}
          />
        </Modal>
      </>
    );
  }

  return (
    <Popover position={'bottom'} opened={open} zIndex={9000} offset={24}>
      <Popover.Target>
        <Stack>
          <NotificationButton
            notifications={notifications}
            setNotifications={setNotifications}
            openHandler={openHandler}
          />
        </Stack>
      </Popover.Target>
      <Popover.Dropdown>
        <Box w={320} ref={popoverRef}>
          <Group m={16}>
            <DMText
              className={classes.notificationTitle}
              dColor={themeVariables.darkMode.strong}
            >
              알림
            </DMText>
          </Group>
          <ScrollArea h={500} type={'never'} scrollbarSize={4}>
            <NotificationBody
              notifications={notifications}
              classes={classes}
              setNotifications={setNotifications}
              openHandler={openHandler}
            />
          </ScrollArea>
        </Box>
      </Popover.Dropdown>
    </Popover>
  );
};

export default NotificationBell;

const NotificationBody = ({
  notifications,
  setNotifications,
  classes,
  openHandler,
}) => {
  return (
    <>
      {notifications.totalCount == 0 ? (
        <DMText
          className={classes.emptyContentText}
          color={themeVariables.darkMode.normal}
        >
          새로운 알림이 없습니다.
        </DMText>
      ) : (
        <NotificationItems
          notifications={notifications}
          setNotifications={setNotifications}
          classes={classes}
          openHandler={openHandler}
        />
      )}
    </>
  );
};

const NotificationButton = ({
  notifications,
  setNotifications,
  openHandler,
}) => {
  const { dark } = isDark();

  return (
    <ActionIcon
      size={36}
      onClick={async () => {
        const [, notificationReq] = await Promise.all([
          API_ENDPOINT.notification.updateRedDot(),
          API_ENDPOINT.notification.getNotifications(5, 1),
        ]);

        setNotifications({
          redDot: false,
          data: notificationReq.data.notifications,
          totalCount: notificationReq.data.total_count,
        });
        openHandler.toggle();
        mixpanelTrack('NotificationBell', 'Clicked');
      }}
      aria-label="bell"
    >
      <Indicator
        disabled={!notifications.redDot}
        color={'red'}
        processing
        size={6}
      >
        <IconBell
          size={21}
          stroke={2}
          color={themeVariables.darkMode.neutral(dark)}
        />
      </Indicator>
    </ActionIcon>
  );
};

const NotificationItems = ({
  notifications,
  setNotifications,
  classes,
  openHandler,
}) => {
  const { dark } = isDark();
  const [notificationPage, setNotificationPage] = useState(1);

  const handleNotificationItemClick = (id) => {
    API_ENDPOINT.notification.readNotification(id);
    setNotifications({
      ...notifications,
      data: notifications?.data?.map((item) => {
        return item.id == id ? { ...item, is_read: true } : item;
      }),
    });
    mixpanelTrack('NotificationBell_Item', 'Clicked', { id: id });
  };

  const handleMoreButtonClick = () => {
    API_ENDPOINT.notification
      .getNotifications(5, notificationPage + 1)
      .then((res) => {
        setNotifications({
          ...notifications,
          data: [...notifications.data, ...res.data.notifications],
        });
        setNotificationPage(notificationPage + 1);
      });
  };

  return (
    <>
      {notifications?.data?.map((item, index) => (
        <div key={item.id} onClick={() => openHandler.close()}>
          <Link
            href={`/${item.path}`}
            onClick={() => handleNotificationItemClick(item.id)}
          >
            <div className={classes.notificationItemContainer}>
              <div className={classes.notificationItemHeader}>
                {!item.is_read && (
                  <div className={classes.notificationItemDot} />
                )}
                <DMText dColor={themeVariables.darkMode.normal}>
                  {item.text}
                </DMText>
              </div>
              <DMText
                className={classes.notificationItemFooter}
                dColor={themeVariables.darkMode.alternative}
              >
                {timeForToday(item.created_at)}
              </DMText>
            </div>
          </Link>
          {index != notifications.data.length - 1 && (
            <Divider my={12} color={themeVariables.lineDark.light200(dark)} />
          )}
        </div>
      ))}
      {notifications.totalCount - notifications.data.length > 0 && (
        <div className={classes.showMoreButtonContainer}>
          <Button
            type={ButtonTypes.gray}
            onClick={handleMoreButtonClick}
            style={{ height: 38 }}
          >
            더 보기
          </Button>
        </div>
      )}
    </>
  );
};
